// import './AppLayout.scss';
import AdminSidemenu from "../../Components/AdminSidemenu";
import * as React from "react";
import { styled, useTheme, Theme, CSSObject } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import {
  FaBarsStaggered,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa6"; // Font Awesome icons
import { Link, Outlet, useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import { common, grey } from "@mui/material/colors";
import Avatar from "@mui/material/Avatar";
import ListItemIcon from "@mui/material/ListItemIcon";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import { FiLock, FiLogOut, FiUsers } from "react-icons/fi";
import Menu from "@mui/material/Menu";
import Badge from "@mui/material/Badge";
import StyledBadge from "../../Components/StyledBadge";
import { useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../Redux/Slices/AuthSlice";
import { useLogoutUserMutation } from "../../Services/RolesService";

const drawerWidth = 260;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  backgroundColor: "white",
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  backgroundColor: "white", // Set Drawer background color to primary color
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": {
      backgroundColor: "white", // Ensure Drawer paper also uses primary color
      ...openedMixin(theme),
    },
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": {
      backgroundColor: "white", // Ensure Drawer paper also uses primary color
      ...closedMixin(theme),
    },
  }),
}));

const isMobileScreen = () => window.innerWidth <= 768; // Adjust breakpoint as needed

const AdminLayouts: React.FC = () => {
  const theme = useTheme();
  const [open, setOpen] = useState(!isMobileScreen());
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null); // State for managing submenus
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [logoutUser, { isLoading }] = useLogoutUserMutation();
  const handleDrawerOpen = () => setOpen(true);
  const handleDrawerClose = () => setOpen(false);
  const isProfileOpened = Boolean(anchorEl);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const isAuthenticated = useSelector(
    (state: any) => state.AuthSlice.isAuthenticated
  );
  const authData = useSelector((state: any) => state.AuthSlice.adminAuthUser);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const toggleSubmenu = (submenu: string) => {
    setOpenSubmenu((prev) => (prev === submenu ? null : submenu));
  };

  useEffect(() => {
    const handleResize = () => setOpen(!isMobileScreen());
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  /**
   * @HANDEL_LOGOUT_FROM_ANOTHER_TAB
   */
  useEffect(() => {
    const syncLogout = (event: StorageEvent) => {
      if (event.key === "logout-event") {
        // Perform logout logic here
        // Clear auth state, redirect to login, etc.
        handleLogout();
      }
    };

    window.addEventListener("storage", syncLogout);
    return () => window.removeEventListener("storage", syncLogout);
  }, []);

  const handleLogout = async () => {
    try {
      const refreshToken =
        localStorage.getItem(
          `${process.env.REACT_APP_STORAGE_KEY}/refreshToken`
        ) ?? "";

      await logoutUser(refreshToken).unwrap();
      localStorage.setItem("logout-event", Date.now().toString());
      dispatch(logout());
      navigate("//login", { replace: true });
      // window.location.href = "/#//login";
    } catch (error) {
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("//login", { replace: true });
    }

    return () => {
      // console.log("logout happen");
    };
  }, [isAuthenticated]);

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar position="fixed" open={open}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{ marginRight: 5, ...(open && { display: "none" }) }}
          >
            <FaBarsStaggered color={theme.palette.primary.main} size={20} />
          </IconButton>
          <img
            src={`${process.env.PUBLIC_URL}/imgs/logo.svg`}
            style={{ height: 25 }}
          />

          <Box sx={{ flexGrow: 1 }}></Box>
          <Box
            className="right-side"
            sx={{ display: "flex", alignItems: "center", flexGrow: 0 }}
          >
            <Button
              onClick={handleClick}
              color="inherit"
              sx={{
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.001)", // Light transparent background on hover
                },
              }}
              startIcon={
                <Avatar
                  src={process.env.PUBLIC_URL + "/imgs/users.png"}
                  sx={{ width: 32, height: 32 }}
                />
              }
            >
              <Box
                sx={{
                  textAlign: "left",
                  display: { xs: "none", sm: "flex" },
                  flexDirection: "column",
                  // justifyContent: 'center',
                  // alignItems: 'center',
                  gap: 2,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ textTransform: "capitalize" }}
                  color="text.secondary"
                >
                  {authData.email}
                </Typography>
                <Typography
                  variant={"subtitle2"}
                  sx={{
                    mt: -7,
                    textTransform: "capitalize",
                    fontWeight: "500",
                  }}
                  color={common.black}
                >
                  {" "}
                  {authData.firstName} {authData.lastName}
                </Typography>
              </Box>
            </Button>

            <Menu
              anchorEl={anchorEl}
              open={isProfileOpened}
              onClose={handleClose}
              slotProps={{
                paper: {
                  elevation: 3,
                  sx: {
                    overflow: "visible",
                    filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                    borderTopLeftRadius: 0,
                    borderTopRightRadius: 0,
                    minWidth: 270,
                    backgroundColor: common.white,
                    mt: 1.35,
                    "& .MuiAvatar-root": {
                      width: 32,
                      height: 32,
                      ml: -0.5,
                      mr: 1,
                    },
                  },
                },
              }}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            >
              <Box
                sx={{
                  display: "flex", // Enable flexbox
                  justifyContent: "center", // Center horizontally
                  alignItems: "center",
                  flexDirection: "column",
                  my: 1.5,
                  px: 1.5,
                }}
              >
                <StyledBadge
                  overlap="circular"
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  variant="dot"
                >
                  <Avatar
                    alt="Remy Sharp"
                    sx={{ border: "1px solid #E3E3E3" }}
                    src={process.env.PUBLIC_URL + "/imgs/users.png"}
                  />
                </StyledBadge>
                <Typography variant="subtitle2" noWrap>
                  {authData.firstName} {authData.lastName}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {authData.email}{" "}
                </Typography>
              </Box>

              <Divider />
              <Box sx={{ padding: 1 }}>
                <MenuItem
                  sx={{ borderRadius: 1.5 }}
                  component={Link}
                  to="/user-profile"
                >
                  <ListItemIcon>
                    <FiUsers fontSize="small" />
                  </ListItemIcon>
                  Profile
                </MenuItem>
                <MenuItem
                  sx={{ borderRadius: 1.5 }}
                  component={Link}
                  to="/change-password"
                >
                  <ListItemIcon>
                    <FiLock fontSize="small" />
                  </ListItemIcon>
                  Change Password
                </MenuItem>

                {/* onClick={handleLogout} */}
                <MenuItem
                  onClick={() => handleLogout()}
                  sx={{ borderRadius: 1.5 }}
                >
                  <ListItemIcon>
                    <FiLogOut fontSize="small" />
                  </ListItemIcon>
                  Logout
                </MenuItem>
              </Box>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer variant="permanent" open={open}>
        <DrawerHeader>
          <IconButton onClick={handleDrawerClose}>
            <FaBarsStaggered color={theme.palette.primary.main} size={20} />
          </IconButton>
        </DrawerHeader>
        <Divider />

        <AdminSidemenu open={open} />
      </Drawer>

      <Box
        component="main"
        style={{
          display: "flex",
          flex: "1 1 auto",
          flexDirection: "column",
          overflowY: "auto",
          maxHeight: "100vh",
        }}
        sx={{
          flexGrow: 1,
          px: 1.5,
          py: 0,
          backgroundColor: grey[100],
          [theme.breakpoints.down("sm")]: {
            px: (theme) => theme.spacing(0.5),
          },
        }}
      >
        <DrawerHeader />
        <Box
          sx={{
            display: "flex",
            flex: "1 1 auto",
            flexDirection: "column",
            pt: 2,
            minHeight: "calc(100vh - 64px)",
            // height:'100%'
          }}
        >
          <Outlet />

          <Box style={{ minHeight: 20 }} />
        </Box>
      </Box>
    </Box>
  );
};

export default AdminLayouts;
